/**
 * 開発実績（WORKS）セクション
 */
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./ui.tsx";

type Work = {
  name: string;
  tagline: string;
  desc: string;
  tags: string[];
  /** 公開URL。空文字の間はリンクボタンを表示しない */
  url: string;
  /** ブラウザバーに表示するドメイン表記 */
  displayUrl: string;
  /** public/ 配下のスクリーンショット画像 */
  image: string;
};

const WORKS: Work[] = [
  {
    name: "Docsmith",
    tagline: "業務書類作成サービス",
    desc: "見積書・納品書・請求書・領収書などの業務書類をブラウザだけで作成できる、フリーランス・ひとり法人のための書類作成サービスです。変換ボタンひとつで「見積書から請求書」がつながり、転記や二度打ちをなくします。インボイス・電子帳簿保存法にも対応。",
    tags: ["自社プロダクト", "SaaS", "業務効率化"],
    url: "https://docsmith.banarylab.com/",
    displayUrl: "docsmith.banarylab.com",
    image: "/works/docsmith.png",
  },
];

/** 実スクリーンショットをブラウザ風フレームで見せるモックアップ */
function BrowserMock({ work }: { work: Work }) {
  return (
    <motion.div
      whileHover={{ scale: 1.03, rotate: -1 }}
      transition={{ type: "spring", stiffness: 200, damping: 16 }}
      className="relative"
    >
      {/* 背面のグロー */}
      <div className="absolute -inset-6 rounded-3xl bg-sky-500/15 blur-2xl" />

      <div className="relative rounded-2xl border border-white/10 bg-slate-900 shadow-2xl shadow-black/40 overflow-hidden">
        {/* ブラウザバー */}
        <div className="flex items-center gap-1.5 px-4 py-3 bg-slate-800/80 border-b border-white/5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
          <div className="ml-3 flex-1 max-w-[260px] h-6 rounded-md bg-slate-700/60 flex items-center px-3">
            <span className="font-en text-[10px] tracking-wider text-slate-400 truncate">
              {work.displayUrl}
            </span>
          </div>
        </div>

        {/* 実際の画面スクリーンショット */}
        <div className="overflow-hidden">
          <motion.img
            src={work.image}
            alt={`${work.name} の画面`}
            loading="lazy"
            className="block w-full"
            initial={{ scale: 1.06, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        </div>
      </div>
    </motion.div>
  );
}

export default function WorksSection() {
  return (
    <section className="relative overflow-hidden bg-ink text-white py-20 md:py-24">
      {/* 背景の装飾 */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="absolute -top-32 left-[-10%] w-96 h-96 rounded-full bg-sky-500/10 blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-6">
        <Reveal className="text-center mb-14">
          <p className="font-en text-sm font-semibold tracking-widest2 text-brand-400 uppercase mb-3">
            Works
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">開発実績</h2>
          <p className="mt-4 text-slate-400 leading-relaxed max-w-2xl mx-auto">
            自社で企画・開発したプロダクトをご紹介します。
          </p>
        </Reveal>

        {WORKS.map((work, i) => (
          <div
            key={work.name}
            className={`grid md:grid-cols-2 gap-10 md:gap-14 items-center ${i > 0 ? "mt-20" : ""}`}
          >
            {/* テキスト側 */}
            <Reveal className={i % 2 === 1 ? "md:order-2" : ""}>
              <p className="font-en text-xs font-semibold tracking-widest2 text-sky-400 uppercase mb-3">
                {work.tagline}
              </p>
              <h3 className="font-en text-3xl md:text-4xl font-bold mb-5">{work.name}</h3>
              <p className="text-slate-300 leading-relaxed mb-6">{work.desc}</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {work.tags.map(tag => (
                  <span
                    key={tag}
                    className="text-xs font-medium text-slate-300 border border-white/15 rounded-full px-3 py-1.5"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              {work.url && (
                <a
                  href={work.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group btn-ghost-light !px-6 !py-3 text-sm"
                >
                  サイトを見る
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              )}
            </Reveal>

            {/* スクリーンショット側 */}
            <Reveal delay={0.15} className={i % 2 === 1 ? "md:order-1" : ""}>
              <BrowserMock work={work} />
            </Reveal>
          </div>
        ))}
      </div>
    </section>
  );
}
